import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Users, Tags, Settings as Sett, Activity, Plus, Gift, Receipt, Upload, Trash2, CheckCircle, XCircle, AlertCircle, Coins, ClipboardCheck, ChevronDown, Ticket, Wallet, UserCog, Building2, MessageSquare } from "lucide-react";
import { initials, timeAgo, canAccessAdmin, isOwner, isDeptAdmin } from "@/lib/utils";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";

const ROLES = [
  { value: "owner", label: "owner" },
  { value: "department_admin", label: "department admin" },
  { value: "contributor", label: "contributor" },
];

const EXAMPLE_CATEGORIES = [
  "Hiring", "Networking", "Partnership", "Market Intelligence", "General Help",
];
const EXAMPLE_DEPARTMENTS = [
  "Engineering", "Sales", "Marketing", "Product", "HR",
];

function nameFromEmail(email) {
  const local = email.split("@")[0] || "";
  return local
    .split(/[._\-+]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
}

function newImportRow() {
  return { id: Math.random().toString(36).slice(2), email: "", department: "" };
}

const ACTIVITY_CATEGORY_ICON = {
  ticket: Ticket,
  bounty: Coins,
  solution: MessageSquare,
  budget: Wallet,
  people: UserCog,
  credits: Coins,
  org: Building2,
};

const ACTIVITY_DETAIL_LABELS = {
  ticket: "Ticket",
  department: "Department",
  status: "Status",
  bounty: "Bounty",
  amount: "Amount",
  credits: "Credits",
  feedback: "Feedback",
  reason: "Reason",
  owner_message: "Owner reply",
  awarded_credits: "Awarded",
  max_per_ticket: "Max per ticket",
  allocated_credits: "Allocated",
  role: "Role",
  user: "User",
  new_title: "New title",
  new_bounty_credits: "New bounty",
  new_status: "New status",
  new_visibility: "New visibility",
  new_difficulty: "New difficulty",
};

function ActivityCategoryIcon({ category }) {
  const Icon = ACTIVITY_CATEGORY_ICON[category] || Activity;
  return <Icon className="h-4 w-4" />;
}

export default function Admin() {
  const { org, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [orgForm, setOrgForm] = useState({ name: "", credit_value_inr: 5, cash_redemption_enabled: true });
  const [rewards, setRewards] = useState([]);
  const [rewardForm, setRewardForm] = useState({ name: "", credits: 100, image: "", stock: 50 });
  const [redemptions, setRedemptions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [budgets, setBudgets] = useState([]);
  const [budgetEdits, setBudgetEdits] = useState({});
  const [budgetIncreaseRequests, setBudgetIncreaseRequests] = useState([]);
  const [budgetRequestForm, setBudgetRequestForm] = useState({ amount: "", message: "" });
  const [budgetDecideMsgs, setBudgetDecideMsgs] = useState({});
  const [budgetSubTab, setBudgetSubTab] = useState(() => {
    const b = searchParams.get("budget");
    if (b === "request" || b === "approved" || b === "departments") return b;
    return isDeptAdmin(user) ? "request" : "departments";
  });
  const [expandedActivity, setExpandedActivity] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [bountyRejectMsgs, setBountyRejectMsgs] = useState({});
  const [activityItems, setActivityItems] = useState([]);
  const [creditFlow, setCreditFlow] = useState(null);

  // Bulk import state
  const [importRows, setImportRows] = useState([newImportRow()]);
  const [bulkDept, setBulkDept] = useState("");
  const [csvText, setCsvText] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const owner = isOwner(user);
  const deptAdmin = isDeptAdmin(user);
  const tabFromUrl = searchParams.get("tab");
  const defaultTab = tabFromUrl || (owner ? "users" : "approvals");

  const load = async () => {
    if (owner) {
      const [a, b, d, e, f, g, i, bir] = await Promise.all([
        api.get("/users"), api.get("/org/categories"),
        api.get("/rewards/admin"), api.get("/redemptions/admin"), api.get("/org/departments"),
        api.get("/department-budgets"),
        api.get("/dashboard/stats"),
        api.get("/budget-increase-requests"),
      ]);
      setUsers(a.data); setCats(b.data); setRewards(d.data);
      setRedemptions(e.data); setDepartments(f.data); setBudgets(g.data);
      setCreditFlow(i.data.credit_flow || null);
      setBudgetIncreaseRequests(bir.data);
      const act = await api.get("/activity", { params: { scope: "org", limit: 40 } }).catch(() => ({ data: [] }));
      setActivityItems(act.data);
    } else if (deptAdmin) {
      const [a, f, g, h, act, bir] = await Promise.all([
        api.get("/users", { params: { department: user.department } }),
        api.get("/org/departments"),
        api.get("/department-budgets"),
        api.get("/requests", { params: { status: "pending_approval" } }),
        api.get("/activity", { params: { scope: "department", limit: 40 } }),
        api.get("/budget-increase-requests"),
      ]);
      setUsers(a.data); setDepartments(f.data); setBudgets(g.data);
      setPendingApprovals(h.data); setActivityItems(act.data);
      setBudgetIncreaseRequests(bir.data);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (org) setOrgForm({
      name: org.name,
      credit_value_inr: org.credit_value_inr || 5,
      cash_redemption_enabled: org.cash_redemption_enabled !== false,
    });
  }, [org]);

  if (!canAccessAdmin(user)) return <div className="text-sm text-slate-500">Owner or department admin only.</div>;

  const visibleUsers = owner
    ? users
    : users.filter((u) => u.department === user.department);

  const roleOptions = owner
    ? ROLES
    : ROLES.filter((r) => r.value !== "owner");

  const changeRole = async (uid, role) => {
    if (uid === user.id) return toast.error("You cannot change your own role");
    try {
      await api.patch(`/users/${uid}/role`, { role });
      toast.success(role === "department_admin" ? "Set as department admin" : "Role updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const changeDepartment = async (uid, department) => {
    try {
      const { data } = await api.patch(`/users/${uid}/department`, { department });
      const isDeptAdminRole = data?.role === "department_admin";
      toast.success(
        isDeptAdminRole
          ? `Department updated — they now admin ${department}`
          : "Department updated"
      );
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const deleteUser = async (u) => {
    if (u.id === user.id) return toast.error("You cannot delete your own account");
    if (!window.confirm(`Delete "${u.name}" (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const addCategory = async (name) => {
    const value = (typeof name === "string" ? name : newCat).trim();
    if (!value) return;
    try {
      await api.post("/org/categories", { name: value });
      setNewCat(""); load();
      toast.success("Category added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const deleteCategory = async (name) => {
    if (!window.confirm(`Remove category "${name}"?`)) return;
    try {
      await api.delete(`/org/categories/${encodeURIComponent(name)}`);
      load();
      toast.success("Category removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const saveOrg = async () => {
    try {
      await api.patch("/org", {
        name: orgForm.name,
        credit_value_inr: Number(orgForm.credit_value_inr),
        cash_redemption_enabled: orgForm.cash_redemption_enabled,
      });
      toast.success("Organization updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const addReward = async () => {
    if (!rewardForm.name.trim()) return toast.error("Reward name is required");
    try {
      await api.post("/rewards", {
        name: rewardForm.name.trim(),
        credits: Number(rewardForm.credits),
        image: rewardForm.image.trim() || null,
        stock: Number(rewardForm.stock),
        reward_type: "catalog",
      });
      setRewardForm({ name: "", credits: 100, image: "", stock: 50 });
      load();
      toast.success("Reward added to catalog");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const setRedemptionStatus = async (id, status) => {
    try {
      await api.patch(`/redemptions/${id}/status`, { status });
      load();
      toast.success(
        status === "fulfilled" ? "Redemption marked fulfilled"
          : status === "rejected" ? "Redemption rejected — credits refunded"
          : "Redemption reopened"
      );
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const toggleReward = async (reward) => {
    try {
      await api.patch(`/rewards/${reward.id}`, { active: !reward.active });
      load();
      toast.success(reward.active ? "Reward deactivated" : "Reward activated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const deleteReward = async (reward) => {
    if (!window.confirm(`Delete "${reward.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/rewards/${reward.id}`);
      load();
      toast.success("Reward deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const addDepartment = async (name) => {
    const value = (typeof name === "string" ? name : newDept).trim();
    if (!value) return;
    try {
      const res = await api.post("/org/departments", { name: value });
      setDepartments(res.data);
      setNewDept("");
      toast.success("Department added — assign a department admin in Users");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const deleteDepartment = async (name) => {
    if (!window.confirm(`Remove department "${name}"? Users must be reassigned first.`)) return;
    try {
      await api.delete(`/org/departments/${encodeURIComponent(name)}`);
      load();
      toast.success("Department removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const saveBudgetAllocate = async (department) => {
    const edit = budgetEdits[department] || {};
    const current = budgets.find((b) => b.department === department);
    try {
      await api.put("/department-budgets", {
        department,
        allocated_credits: Number(edit.allocated_credits ?? current?.allocated_credits ?? 0),
        max_credits_per_ticket: Number(edit.max_credits_per_ticket ?? current?.max_credits_per_ticket ?? 500),
      });
      toast.success(`Budget updated for ${department}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const saveMaxOnly = async (department) => {
    const edit = budgetEdits[department] || {};
    const current = budgets.find((b) => b.department === department);
    try {
      await api.patch(`/department-budgets/${encodeURIComponent(department)}/max`, {
        max_credits_per_ticket: Number(edit.max_credits_per_ticket ?? current?.max_credits_per_ticket ?? 500),
      });
      toast.success(`Max per ticket updated for ${department}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const submitBudgetIncreaseRequest = async () => {
    const amount = Number(budgetRequestForm.amount);
    const message = (budgetRequestForm.message || "").trim();
    if (!amount || amount < 1) return toast.error("Enter credits to request");
    if (!message) return toast.error("Add a message for the owner");
    try {
      await api.post("/budget-increase-requests", { amount, message });
      setBudgetRequestForm({ amount: "", message: "" });
      toast.success("Budget increase requested");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const decideBudgetIncrease = async (requestId, action) => {
    const message = (budgetDecideMsgs[requestId] || "").trim();
    if (!message) return toast.error("Add a reply message");
    try {
      await api.post(`/budget-increase-requests/${requestId}/decide`, { action, message });
      setBudgetDecideMsgs((prev) => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
      toast.success(action === "approve" ? "Budget increase approved" : "Budget increase rejected");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const decideBounty = async (rid, action) => {
    try {
      const payload = { action };
      if (action === "reject") {
        const feedback = (bountyRejectMsgs[rid] || "").trim();
        if (!feedback) return toast.error("Add a rejection message for the creator");
        payload.feedback = feedback;
      }
      await api.post(`/requests/${rid}/approve-bounty`, payload);
      setBountyRejectMsgs((prev) => {
        const next = { ...prev };
        delete next[rid];
        return next;
      });
      toast.success(action === "approve" ? "Ticket published" : "Request rejected");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const updateRow = (id, field, value) => {
    setImportRows((rows) => rows.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };
  const addRow = () => setImportRows((rows) => [...rows, newImportRow()]);
  const removeRow = (id) => setImportRows((rows) => rows.filter((r) => r.id !== id));

  const parseCsv = () => {
    const lines = csvText.trim().split("\n").filter(Boolean);
    const parsed = [];
    for (const line of lines) {
      const [emailRaw, deptRaw] = line.split(",").map((s) => s.trim());
      if (emailRaw && emailRaw.includes("@")) {
        parsed.push({
          id: Math.random().toString(36).slice(2),
          email: emailRaw.toLowerCase(),
          department: deptAdmin ? (user.department || "") : (deptRaw || ""),
        });
      }
    }
    if (parsed.length === 0) return toast.error("No valid emails found in CSV");
    setImportRows(parsed);
    setCsvText("");
    toast.success(`Parsed ${parsed.length} rows`);
  };

  const runImport = async () => {
    const contributors = importRows
      .filter((r) => r.email.trim())
      .map((r) => ({
        email: r.email.trim().toLowerCase(),
        department: deptAdmin
          ? user.department
          : (r.department.trim() || undefined),
      }));
    if (contributors.length === 0) return toast.error("Add at least one email to import");
    setImporting(true);
    setImportResult(null);
    try {
      const res = await api.post("/users/bulk-import", {
        contributors,
        bulk_department: deptAdmin ? user.department : (bulkDept || undefined),
      });
      setImportResult(res.data);
      if (res.data.summary.created > 0) {
        toast.success(`${res.data.summary.created} contributor(s) imported successfully`);
        load();
        setImportRows([newImportRow()]);
        setBulkDept("");
      } else {
        toast.warning("No new contributors were created");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-600">
          {owner ? "Owner" : "Department Admin"}
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">
          {owner ? "Organization controls" : `${user.department} controls`}
        </h1>
        <p className="text-slate-600 mt-1">
          {owner
            ? "Manage users, department budgets, categories, rewards, and org activity."
            : "Manage your department people, imports, over-max bounty approvals, credit max, and activity."}
        </p>
      </div>

      <Tabs defaultValue={defaultTab} key={defaultTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="h-4 w-4 mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="import" data-testid="admin-tab-import"><Upload className="h-4 w-4 mr-1" /> Import Users</TabsTrigger>
          <TabsTrigger value="budgets" data-testid="admin-tab-budgets">
            <Coins className="h-4 w-4 mr-1" /> Budgets
            {owner && budgetIncreaseRequests.filter((r) => r.status === "pending").length > 0 && (
              <Badge className="ml-1 h-5 min-w-5 px-1" variant="secondary">
                {budgetIncreaseRequests.filter((r) => r.status === "pending").length}
              </Badge>
            )}
          </TabsTrigger>
          {deptAdmin && (
            <TabsTrigger value="approvals" data-testid="admin-tab-approvals">
              <ClipboardCheck className="h-4 w-4 mr-1" /> Approvals
              {pendingApprovals.length > 0 && (
                <Badge className="ml-1 h-5 min-w-5 px-1" variant="secondary">{pendingApprovals.length}</Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="activity" data-testid="admin-tab-activity"><Activity className="h-4 w-4 mr-1" /> Activity</TabsTrigger>
          {owner && (
            <>
              <TabsTrigger value="categories" data-testid="admin-tab-categories"><Tags className="h-4 w-4 mr-1" /> Categories</TabsTrigger>
              <TabsTrigger value="departments" data-testid="admin-tab-departments"><Tags className="h-4 w-4 mr-1" /> Departments</TabsTrigger>
              <TabsTrigger value="rewards" data-testid="admin-tab-rewards"><Gift className="h-4 w-4 mr-1" /> Manage Rewards</TabsTrigger>
              <TabsTrigger value="redemptions" data-testid="admin-tab-redemptions"><Receipt className="h-4 w-4 mr-1" /> Redeem activity</TabsTrigger>
              <TabsTrigger value="settings" data-testid="admin-tab-settings"><Sett className="h-4 w-4 mr-1" /> Exchange Rates</TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {owner && (
                <p className="text-sm text-slate-600 px-4 pt-4">
                  Set each person&apos;s department, then role. You can move a{" "}
                  <span className="font-medium">department admin</span> to another department anytime —
                  they become admin of the new one.
                </p>
              )}
              <div className="divide-y divide-slate-200">
                {visibleUsers.map((u) => (
                  <div key={u.id} className="flex items-center gap-4 p-4 flex-wrap" data-testid={`admin-user-${u.id}`}>
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={u.avatar_url || ""} />
                      <AvatarFallback>{initials(u.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-[140px]">
                      <div className="font-semibold">{u.name}{u.id === user.id && <span className="ml-1 text-xs font-normal text-slate-400">(you)</span>}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </div>
                    {u.email_verified ? (
                      <Badge variant="outline" className="text-xs text-green-700 border-green-300 bg-green-50 gap-1" data-testid={`user-verified-${u.id}`}>
                        <CheckCircle className="h-3 w-3" /> Verified
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-amber-700 border-amber-300 bg-amber-50 gap-1" data-testid={`user-unverified-${u.id}`}>
                        <XCircle className="h-3 w-3" /> Unverified
                      </Badge>
                    )}
                    <div className="font-mono text-xs text-slate-500">{u.credits_earned}c earned</div>
                    {owner && u.role !== "owner" ? (
                      <Select
                        value={u.department || undefined}
                        onValueChange={(v) => changeDepartment(u.id, v)}
                      >
                        <SelectTrigger className="w-40" data-testid={`dept-select-${u.id}`}>
                          <SelectValue placeholder="Set department" />
                        </SelectTrigger>
                        <SelectContent>
                          {departments.length === 0 ? (
                            <div className="px-2 py-1.5 text-xs text-slate-500">Add departments first</div>
                          ) : (
                            departments.map((d) => (
                              <SelectItem key={d} value={d}>{d}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-slate-500 w-28 truncate">
                        {u.role === "owner" ? "—" : (u.department || "—")}
                      </span>
                    )}
                    <Select
                      value={u.role}
                      onValueChange={(v) => changeRole(u.id, v)}
                      disabled={u.id === user.id || (!owner && u.role === "owner")}
                    >
                      <SelectTrigger className="w-44" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {roleOptions.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            <span className="capitalize">{r.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(owner || (deptAdmin && u.department === user.department && u.role !== "owner")) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteUser(u)}
                        disabled={u.id === user.id}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="budgets" className="mt-6 space-y-6">
          {owner && (
            <div className="flex rounded-lg border border-slate-200 overflow-hidden w-fit text-sm" data-testid="budget-sub-tabs">
              <button
                type="button"
                className={`px-4 py-2 ${budgetSubTab === "departments" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                onClick={() => setBudgetSubTab("departments")}
              >
                Departments
              </button>
              <button
                type="button"
                className={`px-4 py-2 ${budgetSubTab === "request" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                onClick={() => setBudgetSubTab("request")}
              >
                Request
                {budgetIncreaseRequests.filter((r) => r.status === "pending").length > 0 && budgetSubTab !== "request" && (
                  <span className="ml-2 font-mono text-[10px] bg-orange-500 text-white rounded-full px-1.5">
                    {budgetIncreaseRequests.filter((r) => r.status === "pending").length}
                  </span>
                )}
              </button>
            </div>
          )}

          {deptAdmin && (
            <div className="flex rounded-lg border border-slate-200 overflow-hidden w-fit text-sm" data-testid="budget-sub-tabs">
              <button
                type="button"
                className={`px-4 py-2 ${budgetSubTab === "request" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                onClick={() => setBudgetSubTab("request")}
              >
                Request
                {budgetIncreaseRequests.filter((r) => r.status === "pending").length > 0 && budgetSubTab !== "request" && (
                  <span className="ml-2 font-mono text-[10px] bg-orange-500 text-white rounded-full px-1.5">
                    {budgetIncreaseRequests.filter((r) => r.status === "pending").length}
                  </span>
                )}
              </button>
              <button
                type="button"
                className={`px-4 py-2 ${budgetSubTab === "approved" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                onClick={() => setBudgetSubTab("approved")}
              >
                Approved
              </button>
            </div>
          )}

          {((owner && budgetSubTab === "departments") || (deptAdmin && budgetSubTab === "request")) && (
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-4">
              <p className="text-sm text-slate-600">
                {owner
                  ? "Allocate credit budgets to departments. Ticket bounties deduct from the remaining pool when published."
                  : "Set the maximum credits a creator in your department can put on a ticket without approval."}
              </p>
              {owner && creditFlow && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="credit-flow-summary">
                  {[
                    ["Allocated", creditFlow.allocated],
                    ["Spent", creditFlow.spent],
                    ["Earned", creditFlow.earned],
                    ["Redeemed", creditFlow.redeemed],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-lg border border-slate-200 p-3">
                      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
                      <div className="font-mono font-semibold mt-1">{val}c</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl">
                {budgets.map((b) => {
                  const edit = budgetEdits[b.department] || {};
                  const pct = b.allocated_credits > 0
                    ? Math.min(100, Math.round((b.spent_credits / b.allocated_credits) * 100))
                    : 0;
                  return (
                    <div key={b.department} className="p-4 space-y-3" data-testid={`budget-row-${b.department}`}>
                      <div className="flex flex-wrap items-end gap-4">
                        <div className="min-w-[140px] flex-1">
                          <div className="font-semibold text-sm">{b.department}</div>
                          <div className="text-xs text-slate-500 font-mono">
                            {b.remaining_credits} remaining · {b.spent_credits} spent of {b.allocated_credits}
                          </div>
                          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        {owner && (
                          <div className="space-y-1">
                            <Label className="text-xs">Allocated</Label>
                            <Input
                              type="number"
                              min="0"
                              className="w-32"
                              value={edit.allocated_credits ?? b.allocated_credits}
                              onChange={(e) => setBudgetEdits((prev) => ({
                                ...prev,
                                [b.department]: { ...prev[b.department], allocated_credits: e.target.value },
                              }))}
                            />
                          </div>
                        )}
                        <div className="space-y-1">
                          <Label className="text-xs">Max per ticket</Label>
                          <Input
                            type="number"
                            min="0"
                            className="w-32"
                            value={edit.max_credits_per_ticket ?? b.max_credits_per_ticket}
                            onChange={(e) => setBudgetEdits((prev) => ({
                              ...prev,
                              [b.department]: { ...prev[b.department], max_credits_per_ticket: e.target.value },
                            }))}
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={() => (owner ? saveBudgetAllocate(b.department) : saveMaxOnly(b.department))}
                          data-testid={`save-budget-${b.department}`}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {budgets.length === 0 && (
                  <div className="p-6 text-sm text-slate-500 text-center">No department budgets yet.</div>
                )}
              </div>
            </CardContent>
          </Card>
          )}

          {deptAdmin && budgetSubTab === "request" && (
            <Card className="border-slate-200" data-testid="budget-increase-request-form">
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-sm">Request increase</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Ask the owner to add credits to {user?.department || "your department"}.
                  </p>
                </div>
                <div className="grid sm:grid-cols-[140px_1fr_auto] gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs">Credits to add</Label>
                    <Input
                      type="number"
                      min="1"
                      data-testid="budget-request-amount"
                      value={budgetRequestForm.amount}
                      onChange={(e) => setBudgetRequestForm((prev) => ({ ...prev, amount: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Message to owner</Label>
                    <Textarea
                      rows={2}
                      data-testid="budget-request-message"
                      value={budgetRequestForm.message}
                      onChange={(e) => setBudgetRequestForm((prev) => ({ ...prev, message: e.target.value }))}
                      placeholder="Why do you need more budget?"
                    />
                  </div>
                  <Button onClick={submitBudgetIncreaseRequest} data-testid="budget-request-submit">
                    Submit request
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {((owner && budgetSubTab === "request") || (deptAdmin && budgetSubTab === "request")) && (
          <Card className="border-slate-200" data-testid="budget-increase-requests">
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-sm">{owner ? "Budget increase requests" : "Pending requests"}</h3>
                <p className="text-sm text-slate-600 mt-1">
                  {owner
                    ? "Review department budget increase requests. Approve to add credits, or reject with a reply."
                    : "Your pending budget increase requests awaiting owner decision."}
                </p>
              </div>
              <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl">
                {(owner
                  ? budgetIncreaseRequests
                  : budgetIncreaseRequests.filter((req) => req.status === "pending")
                ).map((req) => (
                  <div key={req.id} className="p-4 space-y-3" data-testid={`budget-request-${req.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-sm">{req.department}</span>
                          <Badge
                            variant="outline"
                            className={
                              req.status === "pending" ? "border-amber-300 text-amber-700"
                                : req.status === "approved" ? "border-emerald-300 text-emerald-700"
                                  : "border-rose-300 text-rose-700"
                            }
                          >
                            {req.status}
                          </Badge>
                          <span className="font-mono text-xs text-slate-500">+{req.amount}c</span>
                          <span className="text-xs text-slate-400">{timeAgo(req.created_at)}</span>
                        </div>
                        {req.requester && (
                          <div className="text-xs text-slate-500">
                            From {req.requester.name}
                          </div>
                        )}
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{req.message}</p>
                        {req.owner_message && (
                          <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-2">
                            Owner: {req.owner_message}
                          </p>
                        )}
                      </div>
                    </div>
                    {owner && req.status === "pending" && (
                      <div className="space-y-2">
                        <Textarea
                          rows={2}
                          placeholder="Reply message"
                          data-testid={`budget-decide-message-${req.id}`}
                          value={budgetDecideMsgs[req.id] || ""}
                          onChange={(e) => setBudgetDecideMsgs((prev) => ({
                            ...prev,
                            [req.id]: e.target.value,
                          }))}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => decideBudgetIncrease(req.id, "approve")}
                            data-testid={`budget-approve-${req.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-rose-700 border-rose-200"
                            onClick={() => decideBudgetIncrease(req.id, "reject")}
                            data-testid={`budget-reject-${req.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {(owner
                  ? budgetIncreaseRequests.length === 0
                  : budgetIncreaseRequests.filter((req) => req.status === "pending").length === 0
                ) && (
                  <div className="p-6 text-sm text-slate-500 text-center">
                    {owner ? "No budget increase requests yet." : "No pending requests."}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          )}

          {deptAdmin && budgetSubTab === "approved" && (
          <Card className="border-slate-200" data-testid="budget-increase-approved">
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-sm">Approved & decided</h3>
                <p className="text-sm text-slate-600 mt-1">
                  History of your budget increase requests that the owner has approved or rejected.
                </p>
              </div>
              <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl">
                {budgetIncreaseRequests
                  .filter((req) => req.status === "approved" || req.status === "rejected")
                  .map((req) => (
                  <div key={req.id} className="p-4 space-y-3" data-testid={`budget-request-${req.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-sm">{req.department}</span>
                          <Badge
                            variant="outline"
                            className={
                              req.status === "approved" ? "border-emerald-300 text-emerald-700"
                                : "border-rose-300 text-rose-700"
                            }
                          >
                            {req.status}
                          </Badge>
                          <span className="font-mono text-xs text-slate-500">+{req.amount}c</span>
                          <span className="text-xs text-slate-400">{timeAgo(req.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{req.message}</p>
                        {req.owner_message && (
                          <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-2">
                            Owner: {req.owner_message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {budgetIncreaseRequests.filter((req) => req.status === "approved" || req.status === "rejected").length === 0 && (
                  <div className="p-6 text-sm text-slate-500 text-center">No decided requests yet.</div>
                )}
              </div>
            </CardContent>
          </Card>
          )}
        </TabsContent>

        {deptAdmin && (
        <TabsContent value="approvals" className="mt-6">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-200">
                {pendingApprovals.map((r) => (
                  <div key={r.id} className="p-4 space-y-3" data-testid={`approval-${r.id}`}>
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <Link to={`/app/requests/${r.id}`} className="font-semibold text-sm hover:text-blue-600">
                          {r.title}
                        </Link>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {r.creator?.name || "Creator"} · {r.department} ·{" "}
                          <span className="font-mono">{r.requested_bounty_credits || r.bounty_credits}c</span> requested
                        </div>
                      </div>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decideBounty(r.id, "approve")}>
                        Approve
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Textarea
                        rows={2}
                        placeholder="Rejection message for the creator (required to reject)"
                        data-testid={`bounty-reject-message-${r.id}`}
                        value={bountyRejectMsgs[r.id] || ""}
                        onChange={(e) => setBountyRejectMsgs((prev) => ({
                          ...prev,
                          [r.id]: e.target.value,
                        }))}
                      />
                      <Button size="sm" variant="outline" onClick={() => decideBounty(r.id, "reject")}>
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
                {pendingApprovals.length === 0 && (
                  <div className="p-8 text-center text-sm text-slate-500">No pending bounty approvals.</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        <TabsContent value="activity" className="mt-6">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
                {activityItems.map((item) => {
                  const key = `${item.kind}-${item.id}`;
                  const open = !!expandedActivity[key];
                  const details = item.details || {};
                  const detailEntries = Object.entries(details).filter(([, v]) => v != null && v !== "");
                  const hasExpand = !!(item.link || item.target_label || detailEntries.length);
                  const credits = details.credits ?? item.meta?.credits ?? details.bounty ?? details.amount;
                  const dept = details.department ?? item.meta?.department;
                  return (
                    <div key={key} className="p-4 text-sm" data-testid={`activity-item-${item.id}`}>
                      <button
                        type="button"
                        className="w-full flex items-start gap-3 text-left"
                        onClick={() => hasExpand && setExpandedActivity((prev) => ({ ...prev, [key]: !prev[key] }))}
                        disabled={!hasExpand}
                      >
                        <div className="mt-0.5 h-8 w-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                          <ActivityCategoryIcon category={item.category} />
                        </div>
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarImage src={item.actor?.avatar_url || ""} />
                          <AvatarFallback className="text-[8px]">{initials(item.actor?.name || "?")}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-800 leading-snug">
                            {item.summary || (
                              <>
                                <span className="font-medium">{item.actor?.name || "System"}</span>
                                <span className="text-slate-500"> · </span>
                                <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{item.action}</span>
                              </>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-1.5">
                            {credits != null && (
                              <span className="font-mono text-[11px] text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
                                {credits}c
                              </span>
                            )}
                            {dept && (
                              <span className="text-[11px] text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">{dept}</span>
                            )}
                            {item.category && (
                              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{item.category}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-400">{timeAgo(item.timestamp)}</span>
                          {hasExpand && (
                            <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                          )}
                        </div>
                      </button>
                      {open && hasExpand && (
                        <div className="mt-3 ml-11 pl-3 border-l-2 border-slate-200 space-y-2" data-testid={`activity-expand-${item.id}`}>
                          {(item.target_label || item.link) && (
                            <div className="text-sm">
                              {item.link ? (
                                <Link to={item.link} className="font-medium text-blue-700 hover:underline" onClick={(e) => e.stopPropagation()}>
                                  {item.target_label || "Open ticket"}
                                </Link>
                              ) : (
                                <span className="font-medium text-slate-800">{item.target_label}</span>
                              )}
                            </div>
                          )}
                          {detailEntries.length > 0 && (
                            <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 text-xs">
                              {detailEntries.map(([k, v]) => (
                                <div key={k} className="contents">
                                  <dt className="text-slate-500">{ACTIVITY_DETAIL_LABELS[k] || k}</dt>
                                  <dd className="text-slate-800 whitespace-pre-wrap break-words">{String(v)}</dd>
                                </div>
                              ))}
                            </dl>
                          )}
                          {item.action && (
                            <div className="text-[10px] font-mono text-slate-400">{item.action}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {activityItems.length === 0 && (
                  <div className="p-8 text-center text-sm text-slate-500">No recent activity.</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="import" className="mt-6 space-y-6">
          {org && (
            <div className="flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-blue-800">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Only emails with domain <strong>@{org.domain}</strong> can be imported.
                {deptAdmin && user?.department && (
                  <> They will be added to <strong>{user.department}</strong>.</>
                )}{" "}
                An email already registered in another department cannot be imported again.
              </span>
            </div>
          )}

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-display text-base font-semibold mb-1">Paste CSV</h3>
              <p className="text-xs text-slate-500 mb-3">
                {deptAdmin ? (
                  <>One email per line (department column ignored — everyone joins <strong>{user?.department || "your department"}</strong>).</>
                ) : (
                  <>One entry per line: <code className="bg-slate-100 px-1 rounded">email,department</code> — department is optional.</>
                )}
              </p>
              <textarea
                className="w-full h-28 text-sm font-mono border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder={
                  deptAdmin
                    ? "john.doe@acme.com\njane.smith@acme.com"
                    : "john.doe@acme.com,Engineering\njane.smith@acme.com,Sales\nbob@acme.com"
                }
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                data-testid="import-csv-textarea"
              />
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={parseCsv}
                disabled={!csvText.trim()}
                data-testid="import-parse-csv-btn"
              >
                Parse CSV into rows
              </Button>
            </CardContent>
          </Card>

          {owner && (
            <Card className="border-slate-200">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display text-base font-semibold">Bulk settings</h3>
                <Field label="Apply department to all rows (unless overridden per row)">
                  <Select value={bulkDept} onValueChange={setBulkDept}>
                    <SelectTrigger data-testid="import-bulk-dept">
                      <SelectValue placeholder="— pick department —" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </CardContent>
            </Card>
          )}

          {deptAdmin && user?.department && (
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
              Importing into department: <strong>{user.department}</strong>
            </div>
          )}

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-display text-base font-semibold mb-4">Contributor rows</h3>
              <div className="space-y-2">
                <div className={`grid gap-2 text-xs font-medium uppercase tracking-wider text-slate-500 px-1 ${deptAdmin ? "grid-cols-[1fr_120px_32px]" : "grid-cols-[1fr_180px_120px_32px]"}`}>
                  <span>Email</span>
                  {!deptAdmin && <span>Department (optional)</span>}
                  <span>Name preview</span>
                  <span />
                </div>
                {importRows.map((row) => (
                  <div key={row.id} className={`grid gap-2 items-center ${deptAdmin ? "grid-cols-[1fr_120px_32px]" : "grid-cols-[1fr_180px_120px_32px]"}`}>
                    <Input
                      type="email"
                      placeholder={org ? `name@${org.domain}` : "name@company.com"}
                      value={row.email}
                      onChange={(e) => updateRow(row.id, "email", e.target.value)}
                      className="text-sm"
                      data-testid={`import-row-email-${row.id}`}
                    />
                    {!deptAdmin && (
                      <Select
                        value={row.department || "__bulk__"}
                        onValueChange={(v) => updateRow(row.id, "department", v === "__bulk__" ? "" : v)}
                      >
                        <SelectTrigger className="text-sm" data-testid={`import-row-dept-${row.id}`}>
                          <SelectValue placeholder={bulkDept || "— bulk —"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__bulk__">— use bulk —</SelectItem>
                          {departments.map((d) => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <span className="text-xs text-slate-500 truncate">
                      {row.email.includes("@") ? nameFromEmail(row.email) : "—"}
                    </span>
                    <button
                      className="text-slate-400 hover:text-red-500 transition-colors"
                      onClick={() => removeRow(row.id)}
                      title="Remove row"
                      data-testid={`import-row-remove-${row.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={addRow} className="mt-3" data-testid="import-add-row-btn">
                <Plus className="h-4 w-4 mr-1" /> Add row
              </Button>
            </CardContent>
          </Card>

          <div className="flex gap-3 items-center">
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={runImport}
              disabled={importing}
              data-testid="import-submit-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {importing ? "Importing…" : `Import ${importRows.filter((r) => r.email.trim()).length} contributor(s)`}
            </Button>
            <p className="text-xs text-slate-500">
              Each contributor will receive an invite email with a temporary password.
            </p>
          </div>

          {importResult && (
            <Card className="border-slate-200">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display text-base font-semibold">Import results</h3>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1 text-green-700 font-medium">
                    <CheckCircle className="h-4 w-4" /> {importResult.summary.created} created
                  </span>
                  <span className="flex items-center gap-1 text-amber-600 font-medium">
                    <AlertCircle className="h-4 w-4" /> {importResult.summary.skipped} skipped
                  </span>
                  <span className="flex items-center gap-1 text-red-600 font-medium">
                    <XCircle className="h-4 w-4" /> {importResult.summary.errors} errors
                  </span>
                </div>
                {importResult.created.length > 0 && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Created</p>
                    <div className="space-y-1">
                      {importResult.created.map((r) => (
                        <div key={r.email} className="flex items-center gap-3 text-sm">
                          <CheckCircle className="h-3.5 w-3.5 text-green-600 shrink-0" />
                          <span className="font-medium">{r.name}</span>
                          <span className="text-slate-500">{r.email}</span>
                          <Badge variant="outline" className="text-xs">{r.department}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {importResult.skipped.length > 0 && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Skipped (already exists)</p>
                    <div className="space-y-1">
                      {importResult.skipped.map((r) => (
                        <div key={r.email} className="flex items-center gap-3 text-sm text-amber-700">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                          <span>{r.email}</span>
                          <span className="text-xs text-slate-400">{r.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {importResult.errors.length > 0 && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Errors</p>
                    <div className="space-y-1">
                      {importResult.errors.map((r) => (
                        <div key={r.email} className="flex items-center gap-3 text-sm text-red-700">
                          <XCircle className="h-3.5 w-3.5 shrink-0" />
                          <span>{r.email}</span>
                          <span className="text-xs text-slate-400">{r.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {owner && (
        <>
        <TabsContent value="categories" className="mt-6 space-y-6">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-display text-base font-semibold mb-1">Request categories</h3>
              <p className="text-sm text-slate-500 mb-3">
                Categories label tickets on the board. Start empty — add what your org needs.
              </p>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="e.g. Hiring"
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCategory()}
                  data-testid="new-category-input"
                />
                <Button onClick={() => addCategory()} className="bg-blue-600 hover:bg-blue-700" data-testid="add-category-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
              {cats.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-4" data-testid="category-examples">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Examples — click to add</p>
                  <div className="flex flex-wrap gap-2">
                    {EXAMPLE_CATEGORIES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => addCategory(c)}
                        className="text-sm px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 transition-colors"
                      >
                        + {c}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {cats.map((c) => (
                    <Badge key={c} variant="outline" className="text-sm gap-1.5 pr-1" data-testid={`category-${c}`}>
                      {c}
                      <button
                        type="button"
                        aria-label={`Remove ${c}`}
                        className="rounded-full p-0.5 hover:bg-slate-200"
                        onClick={() => deleteCategory(c)}
                        data-testid={`delete-category-${c}`}
                      >
                        <XCircle className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="departments" className="mt-6 space-y-6">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-display text-base font-semibold mb-1">Departments</h3>
              <p className="text-sm text-slate-500 mb-3">
                Add departments, allocate budgets, then assign a department admin in Users.
              </p>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="e.g. Engineering"
                  value={newDept}
                  onChange={(e) => setNewDept(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addDepartment()}
                  data-testid="new-department-input"
                />
                <Button onClick={() => addDepartment()} className="bg-blue-600 hover:bg-blue-700" data-testid="add-department-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
              {departments.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-4" data-testid="department-examples">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Examples — click to add</p>
                  <div className="flex flex-wrap gap-2">
                    {EXAMPLE_DEPARTMENTS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => addDepartment(d)}
                        className="text-sm px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 transition-colors"
                      >
                        + {d}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {departments.map((d) => (
                    <Badge key={d} variant="outline" className="text-sm gap-1.5 pr-1" data-testid={`department-${d}`}>
                      {d}
                      <button
                        type="button"
                        aria-label={`Remove ${d}`}
                        className="rounded-full p-0.5 hover:bg-slate-200"
                        onClick={() => deleteDepartment(d)}
                        data-testid={`delete-department-${d}`}
                      >
                        <XCircle className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rewards" className="mt-6 space-y-6">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-display text-lg font-semibold mb-4">Add reward to catalog</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Reward name">
                  <Input value={rewardForm.name} onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })}
                         placeholder="Amazon Gift Card ₹500" data-testid="admin-reward-name" />
                </Field>
                <Field label="Credits required">
                  <Input type="number" min={1} value={rewardForm.credits}
                         onChange={(e) => setRewardForm({ ...rewardForm, credits: e.target.value })}
                         data-testid="admin-reward-credits" />
                </Field>
                <Field label="Stock">
                  <Input type="number" min={0} value={rewardForm.stock}
                         onChange={(e) => setRewardForm({ ...rewardForm, stock: e.target.value })}
                         data-testid="admin-reward-stock" />
                </Field>
                <Field label="Image URL (optional)">
                  <Input value={rewardForm.image} onChange={(e) => setRewardForm({ ...rewardForm, image: e.target.value })}
                         placeholder="https://..." data-testid="admin-reward-image" />
                </Field>
              </div>
              <Button onClick={addReward} className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="admin-add-reward-btn">
                <Plus className="h-4 w-4 mr-1" /> Add reward
              </Button>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-200">
                {rewards.filter((r) => r.reward_type !== "cash").map((r) => (
                  <div key={r.id} className="flex items-center gap-4 p-4" data-testid={`admin-reward-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.credits}c · {r.stock} in stock</div>
                    </div>
                    <Badge variant={r.active ? "default" : "outline"}>{r.active ? "Active" : "Inactive"}</Badge>
                    <Button size="sm" variant="outline" onClick={() => toggleReward(r)} data-testid={`toggle-reward-${r.id}`}>
                      {r.active ? "Deactivate" : "Activate"}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteReward(r)} data-testid={`delete-reward-${r.id}`}>
                      Delete
                    </Button>
                  </div>
                ))}
                {rewards.filter((r) => r.reward_type !== "cash").length === 0 && (
                  <div className="p-8 text-center text-sm text-slate-500">No rewards yet. Add one above.</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="redemptions" className="mt-6">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
                {redemptions.map((r) => (
                  <div key={r.id} className="flex items-center gap-4 p-4" data-testid={`admin-redemption-${r.id}`}>
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={r.contributor?.avatar_url || ""} />
                      <AvatarFallback>{initials(r.contributor?.name || "?")}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{r.contributor?.name || "—"}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {r.contributor?.department || "—"} · {timeAgo(r.created_at)}
                      </div>
                    </div>
                    <div className="min-w-0 hidden sm:block">
                      <div className="text-sm truncate">{r.reward_name}</div>
                      <div className="text-xs text-slate-500">
                        {r.credits}c{r.redemption_type === "cash" && r.cash_inr ? ` · ₹${r.cash_inr.toLocaleString()}` : ""}
                      </div>
                    </div>
                    <Badge variant="outline" className="capitalize">{r.redemption_type}</Badge>
                    <Badge
                      variant={r.status === "fulfilled" ? "default" : r.status === "rejected" ? "destructive" : "outline"}
                      className="capitalize"
                    >
                      {r.status}
                    </Badge>
                    <Select value={r.status} onValueChange={(v) => setRedemptionStatus(r.id, v)}>
                      <SelectTrigger className="w-32" data-testid={`redemption-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">pending</SelectItem>
                        <SelectItem value="fulfilled">fulfilled</SelectItem>
                        <SelectItem value="rejected">rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {redemptions.length === 0 && (
                  <div className="p-8 text-center text-sm text-slate-500">No redemptions yet.</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <Card className="border-slate-200">
            <CardContent className="p-6 max-w-xl space-y-4">
              <Field label="Organization name">
                <Input value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })} data-testid="settings-org-name" />
              </Field>
              <Field label="Credit valuation (₹ per credit)">
                <Input type="number" value={orgForm.credit_value_inr} onChange={(e) => setOrgForm({ ...orgForm, credit_value_inr: e.target.value })} data-testid="settings-credit-value" />
                <p className="text-xs text-slate-500 mt-1">1 credit = ₹{orgForm.credit_value_inr}. Used for Cashify cash payouts.</p>
              </Field>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={orgForm.cash_redemption_enabled}
                  onChange={(e) => setOrgForm({ ...orgForm, cash_redemption_enabled: e.target.checked })}
                  data-testid="settings-cashify-enabled"
                  className="h-4 w-4 rounded border-slate-300"
                />
                <span className="text-sm">Allow contributors to redeem credits as cash (Cashify)</span>
              </label>
              <Button onClick={saveOrg} className="bg-blue-600 hover:bg-blue-700" data-testid="settings-save">Save settings</Button>
            </CardContent>
          </Card>
        </TabsContent>
        </>
        )}
      </Tabs>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium uppercase tracking-wider">{label}</Label>
    {children}
  </div>
);
